/**
 * Modal Component
 *
 * Shared modal/dialog base with proper accessibility semantics:
 * - role="dialog" + aria-modal="true"
 * - aria-labelledby pointing at the title element
 * - Focus trap (Tab/Shift+Tab cycle within modal)
 * - ESC closes the modal
 * - Backdrop click closes the modal
 * - On open: focus moves to first focusable element (or modal itself)
 * - On close: focus returns to the element that opened it
 */
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called on ESC / backdrop click. Return false to suppress close (e.g. while
   * a submission is in flight). If omitted, close is always permitted.
   */
  canClose?: () => boolean;
  /** Modal title - rendered as heading and linked via aria-labelledby */
  title: React.ReactNode;
  /** Optional subtitle/description displayed below title */
  subtitle?: React.ReactNode;
  /** Optional icon rendered next to title */
  icon?: React.ReactNode;
  /** Optional custom header node (overrides default title/subtitle/icon rendering) */
  header?: React.ReactNode;
  /** Modal body content */
  children: React.ReactNode;
  /** Optional footer (action row) content */
  footer?: React.ReactNode;
  /** Show the default close (X) button in the header */
  showCloseButton?: boolean;
  /** Additional classes on the modal dialog surface */
  className?: string;
  /** Classes to override the max-width / size. Default: max-w-md */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  /** Use a sticky header (good for long scrollable modals). Default: false */
  stickyHeader?: boolean;
  /**
   * Render modal contents inside a fixed-height flex column with a scrollable
   * body. Default: false (content flows naturally, modal is capped at max-h).
   */
  flexBody?: boolean;
  /**
   * Maximum height. Defaults: '85vh' when flexBody=false, '90vh' when flexBody=true.
   */
  maxHeight?: string;
  /** Backdrop styling override. Default: bg-black/50 */
  backdropClassName?: string;
}

const sizeStyles: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
};

// Selector for focusable elements (for focus trap).
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null
  );
}

export function Modal({
  isOpen,
  onClose,
  canClose,
  title,
  subtitle,
  icon,
  header,
  children,
  footer,
  showCloseButton = true,
  className = '',
  size = 'md',
  stickyHeader = false,
  flexBody = false,
  maxHeight,
  backdropClassName,
}: ModalProps): React.ReactElement | null {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const tryClose = useCallback((): void => {
    if (canClose && !canClose()) return;
    onClose();
  }, [canClose, onClose]);

  // Remember what was focused before opening, restore on close/unmount.
  useLayoutEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    return () => {
      const el = previousFocusRef.current;
      if (el && typeof el.focus === 'function' && document.body.contains(el)) {
        // Defer to next tick so React re-render completes first.
        window.requestAnimationFrame(() => {
          el.focus();
        });
      }
    };
  }, [isOpen]);

  // Move focus inside the modal when it opens.
  useEffect(() => {
    if (!isOpen) return;
    // Defer so portal children are mounted.
    const raf = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = getFocusableElements(dialog);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        dialog.focus();
      }
    });
    return () => window.cancelAnimationFrame(raf);
  }, [isOpen]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.left = prev.left;
      document.body.style.right = prev.right;
      document.body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Keyboard handling: ESC to close, Tab focus trap.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        tryClose();
        return;
      }
      if (e.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = getFocusableElements(dialog);
        if (focusable.length === 0) {
          e.preventDefault();
          dialog.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !dialog.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, tryClose]);

  if (!isOpen) return null;

  const computedMaxHeight = maxHeight ?? (flexBody ? '90vh' : '85vh');

  const defaultHeader = (
    <div className="flex items-start gap-3 flex-1 min-w-0">
      {icon && <div className="shrink-0">{icon}</div>}
      <div className="min-w-0 flex-1">
        <h2
          id={titleId}
          className="text-lg font-semibold text-charcoal-900 truncate"
        >
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm text-charcoal-500 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  );

  const dialogClasses = [
    'relative bg-white rounded-2xl w-full animate-slide-up shadow-xl',
    sizeStyles[size],
    flexBody ? 'flex flex-col overflow-hidden' : 'overflow-y-auto',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const dialogStyle: React.CSSProperties = flexBody
    ? { maxHeight: computedMaxHeight }
    : { maxHeight: computedMaxHeight };

  return createPortal(
    <div
      className={`flex items-center justify-center p-4 ${
        backdropClassName ?? 'bg-black/50'
      }`}
      onClick={tryClose}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100dvh',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={dialogClasses}
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`${
            stickyHeader
              ? 'sticky top-0 bg-white z-10'
              : flexBody
                ? 'shrink-0'
                : ''
          } px-6 py-4 border-b border-charcoal-100 flex items-center justify-between gap-3`}
        >
          {header ?? defaultHeader}
          {showCloseButton && (
            <button
              type="button"
              onClick={tryClose}
              className="shrink-0 min-w-11 min-h-11 flex items-center justify-center text-charcoal-500 hover:text-charcoal-700 hover:bg-charcoal-50 active:bg-charcoal-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2"
              aria-label="Schließen"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Body */}
        <div className={flexBody ? 'flex-1 overflow-y-auto' : ''}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className={`${
              flexBody ? 'shrink-0' : ''
            } px-6 py-4 border-t border-charcoal-100`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.getElementById('modal-root') ?? document.body
  );
}

export default Modal;
